import { useQuery } from '@tanstack/react-query';
import { api, type CompStage } from '../lib/api';
import { displayClanName } from '../lib/displayName';

// ─────────────────────────────────────────────────────────────────────────────
// EliminationBracketPanel — T5 (comp-lifecycle-pipeline, elimination gate):
// once POST .../confirm-bracket has flipped an ELIMINATION_SINGLE/
// ELIMINATION_DOUBLE stage LIVE, this renders a QUIET read-only summary in
// its stage card — a slots-played count, and, once the bracket completes, a
// "Champion" line. Deliberately minimal: full bracket play (score entry,
// shot upload/OCR, round-by-round rendering) already lives on the dedicated
// Bracket.tsx page/BracketCanvas — untouched by this thread (HARD SCOPE: no
// OCR, no matchCardV2, no scoreCascade changes) — this card is a pointer,
// not a rebuild of that surface.
//
// Winner derivation: NOTHING new here. GET /comps/:compId/bracket already
// derives `champion` read-only (the final BRACKET# slot's winner, once
// PLAYED) — the SAME derivation portalApi/index.ts's public spectator page
// route independently reuses. BRACKET# rows aren't stage-scoped (an
// existing arc-2 property, unchanged by this thread), so this reads the
// comp-level bracket directly rather than adding a stage-scoped variant.
export function EliminationBracketPanel({ compId, stage }: { compId: string; stage: CompStage }) {
  const { data: bracket } = useQuery({
    queryKey: ['bracket', compId],
    queryFn: () => api.bracket(compId),
    enabled: !!compId,
  });

  if (!bracket || bracket.slots.length === 0) return null;

  const played = bracket.slots.filter((s) => s.status === 'PLAYED' || s.status === 'BYE').length;
  const total = bracket.slots.length;

  return (
    <div className="mt-3 border-t border-white/[0.06] pt-3">
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-meta">Bracket</p>
      {bracket.champion ? (
        <p className="text-sm text-ink-primary">🏆 Champion: <span className="font-medium">{displayClanName(bracket.champion)}</span></p>
      ) : (
        <p className="text-xs text-ink-meta">{played} of {total} slots played.</p>
      )}
      {stage.status === 'LIVE' && !bracket.champion && (
        <p className="mt-1 text-[11px] text-ink-meta">Full bracket play (scores, shot uploads) happens on the Bracket page.</p>
      )}
    </div>
  );
}
